import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { MonthTarget, TargetComponentValue } from "@/types/user/target";

import { allowanceLabel, format_with_unit } from "./utils";

const BONUS_GRADIENTS = [
    "bg-gradient-to-br from-blue-500 to-blue-600",
    "bg-gradient-to-br from-blue-500 to-indigo-600",
    "bg-gradient-to-br from-indigo-500 to-indigo-600",
    "bg-gradient-to-br from-indigo-500 to-purple-600",
    "bg-gradient-to-br from-purple-500 to-purple-600",
    "bg-gradient-to-br from-purple-500 to-pink-600",
    "bg-gradient-to-br from-pink-500 to-pink-600",
    "bg-gradient-to-br from-pink-500 to-red-600",
    "bg-gradient-to-br from-red-500 to-red-600",
    "bg-gradient-to-br from-red-500 to-orange-600",
    "bg-gradient-to-br from-orange-500 to-orange-600",
    "bg-gradient-to-br from-orange-500 to-yellow-600",
    "bg-gradient-to-br from-yellow-500 to-yellow-600",
    "bg-gradient-to-br from-yellow-500 to-green-600",
    "bg-gradient-to-br from-green-500 to-green-600",
    "bg-gradient-to-br from-green-500 to-teal-600",
    "bg-gradient-to-br from-teal-500 to-teal-600",
    "bg-gradient-to-br from-teal-500 to-cyan-600",
    "bg-gradient-to-br from-cyan-500 to-cyan-600",
    "bg-gradient-to-br from-cyan-500 to-blue-600"
];

const PROGRESS_BAR_GRADIENTS = [
    "[&>div]:bg-gradient-to-br [&>div]:from-blue-500 [&>div]:to-blue-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-blue-500 [&>div]:to-indigo-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-indigo-500 [&>div]:to-indigo-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-indigo-500 [&>div]:to-purple-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-purple-500 [&>div]:to-purple-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-purple-500 [&>div]:to-pink-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-pink-500 [&>div]:to-pink-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-pink-500 [&>div]:to-red-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-red-500 [&>div]:to-red-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-red-500 [&>div]:to-orange-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-orange-500 [&>div]:to-orange-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-orange-500 [&>div]:to-yellow-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-yellow-500 [&>div]:to-yellow-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-yellow-500 [&>div]:to-green-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-green-500 [&>div]:to-green-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-green-500 [&>div]:to-teal-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-teal-500 [&>div]:to-teal-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-teal-500 [&>div]:to-cyan-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-cyan-500 [&>div]:to-cyan-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-cyan-500 [&>div]:to-blue-600"
];

interface TargetDetailProps {
    monthTarget: MonthTarget;
    components: TargetComponentValue[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function TargetMonthDetail({ monthTarget, components: allComponentValues, open, onOpenChange }: TargetDetailProps) {
    // Group components by their class and sum all values
    const groupedComponents =
        monthTarget.components
            .filter(component => component.component_class)
            .map(component => {
                const componentValues = allComponentValues.filter(c => c.target_component === component.id);
                const weight = monthTarget.system_class?.weights.find(w => w.name === component.component_class?.name)?.weight ?? 0;

                // Sum all values for this component class
                const totalValues = {
                    current: component.total_current ?? 0,
                    goal: component.total_goal ?? 1
                };

                return {
                    component: component,
                    weight,
                    totalValues,
                    values: componentValues
                };
            }) ?? [];

    // Prepare payplan data for the histogram
    const progress = (monthTarget.current / (monthTarget.goal || 1)) * 100;
    const payplanSteps = monthTarget.system_class?.payplan.steps.sort((a, b) => a.min - b.min);

    const getGradientForBonus = (bonus: number, forProgress: boolean = false) => {
        if (bonus === 0) return forProgress ? "[&>div]:bg-black" : "bg-black";
        const gradientIndex = Math.min(Math.floor(bonus / 100), BONUS_GRADIENTS.length - 1);
        return forProgress ? PROGRESS_BAR_GRADIENTS[gradientIndex] : BONUS_GRADIENTS[gradientIndex];
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-5xl">
                <DialogHeader>
                    <DialogTitle>Quel est ma performance sur le mois complet ?</DialogTitle>
                </DialogHeader>
                <div className="grid gap-5 py-4">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-muted-foreground text-red-700">Les montants indiqués sont donnés à titre indicatif et sous soumis à des ajustements</span>
                        </div>
                        {payplanSteps && (
                            <div className="grid grid-cols-3 gap-4">
                                {payplanSteps.map((step, index) => {
                                    if (progress >= step.min && progress < step.max) {
                                        const currentStep = step;
                                        const currentStepMinPoints = currentStep.min;
                                        const nextSteps = payplanSteps.slice(index + 1, index + 3);
                                        return (
                                            <>
                                                <div key="current" className={`space-y-2 rounded-lg p-4 ${getGradientForBonus(currentStep.bonus)}`}>
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm font-medium text-white">
                                                            Bonus estimé
                                                            {currentStep.bonus > 0 ? ` • ${Math.round(currentStep.min)} points` : null}
                                                        </span>
                                                        <span className="min-w-[60px] text-sm font-semibold text-white">{monthTarget.payplan_bonus}€ bruts</span>
                                                    </div>
                                                    <Progress value={100} className="h-2 [&>div]:bg-white/50" />
                                                    <span className="text-xs text-white/80">{monthTarget.to_date_current} points</span>
                                                </div>
                                                {nextSteps.map((nextStep, nextIndex) => {
                                                    const pointsNeeded = Math.ceil(nextStep.min - monthTarget.current);
                                                    return (
                                                        <div key={nextStep.min} className={`rounded-[10px] p-[5px] ${getGradientForBonus(nextStep.bonus)}`}>
                                                            <div className="h-full space-y-2 rounded-[5px] bg-white p-3">
                                                                <div className="flex items-center justify-between text-black">
                                                                    <span className="text-sm font-medium">
                                                                        Palier {index + nextIndex + 1} • {Math.round(nextStep.min)} points
                                                                    </span>
                                                                    <span className="text-sm font-semibold ">{nextStep.bonus}€ bruts</span>
                                                                </div>
                                                                <Progress
                                                                    value={((monthTarget.current - currentStepMinPoints) / (nextStep.min - currentStepMinPoints)) * 100}
                                                                    className={`h-2 ${getGradientForBonus(nextStep.bonus, true)}`}
                                                                />
                                                                <span className="text-xs text-black/80">{pointsNeeded} points manquants</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        )}
                    </div>
                    <div className="mt-4 space-y-4">
                        <h4 className="font-medium">Décomposition de l'objectif</h4>
                        <div className="grid gap-x-16 gap-y-10 lg:grid-cols-2">
                            {groupedComponents.map(({ component, weight, totalValues }) => {
                                if (!component.component_class) return null;
                                if (weight === 0) return null;
                                const points = component.composite_value ?? 0;
                                const weightedPoints = points * weight;
                                const displayCurrent = component.total_current ?? 0;
                                const displayGoal = component.total_goal ?? 0;

                                const currentRatio = (component.total_to_date_current ?? 0) / (component.total_to_date_goal ?? 1);
                                const nextStep = (() => {
                                    if (component.component_class!.steps?.length) {
                                        return component.component_class!.steps
                                            .slice()
                                            .sort((a, b) => a.min - b.min)
                                            .filter(step => step.composite_value > 0)
                                            .filter(step => step.min > currentRatio)[0];
                                    }
                                    if (component.component_class!.interpolation_points?.length) {
                                        const nextPoint = [...component.component_class!.interpolation_points]
                                            .sort((a, b) => a[0] - b[0])
                                            .filter(p => p[1] > 0)
                                            .filter(p => p[0] > currentRatio)[0];
                                        return nextPoint ? { min: nextPoint[0], composite_value: nextPoint[1] } : undefined;
                                    }
                                    return undefined;
                                })();

                                return (
                                    <div key={component.component_class.name} className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-medium">
                                                    {component.component_class.verbose_name} : {weightedPoints.toFixed(0)} points
                                                    <span className="text-muted-foreground">
                                                        {weight == 1 && ` (×${weight})`}
                                                        {weight != 1 && ` (${Math.round(points)} points × ${weight})`}
                                                    </span>
                                                </p>
                                                {(component.component_class.steps?.length || component.component_class.interpolation_points?.length) ? (
                                                    <span className="text-sm text-muted-foreground">
                                                        {nextStep ? (
                                                            <span>
                                                                Le prochain palier, à {format_with_unit(Math.round(nextStep.min * (component.total_goal ?? 1)), component.component_class.target_unit)},
                                                                vous rapportera {Math.round(nextStep.composite_value - points)} point{Math.round(nextStep.composite_value - points) > 1 && "s"}

                                                            </span>
                                                        ) : (
                                                            "Vous êtes au palier max."
                                                        )}
                                                    </span>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="space-y-2 pr-4">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-muted-foreground">
                                                    {component.component_class.is_penalty
                                                        ? allowanceLabel(displayCurrent, displayGoal)
                                                        : `${format_with_unit(displayCurrent, component.component_class.target_unit)} sur un objectif mensuel de ${format_with_unit(displayGoal, component.component_class.target_unit)}`}
                                                </span>
                                            </div>
                                            <Progress value={(totalValues.current / totalValues.goal) * 100} className="h-2 [&>div]:bg-[#3b82f6]" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
